import { useEffect, useState, useCallback } from "react";
import { Input, Typography, Tag, Flex, Button } from "antd";
import { SearchOutlined, HomeOutlined } from "@ant-design/icons";
import { getMasterList } from "../../../services/adminServices";
import { showLoader } from "../../../utils/showloader";
import MasterList from "./MasterList";
import BreadCrumb from "../../BreadCrumb";
import { useTranslation } from "react-i18next";
import { useTenantNavigation } from "../../../hooks/useTenantNavigation.js";

const { Title } = Typography;

// A page to view all master data with search functionality similar to AllSpacesPage
const AllMasterDataPage = () => {
  const [masterData, setMasterData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { t } = useTranslation();
  const navigate = useTenantNavigation();

  const fetchData = useCallback(async () => {
    showLoader(true);
    const resp = await getMasterList();
    showLoader(false);
    if (resp?.data) {
      setMasterData(resp.data);
      setFilteredData(resp.data);
    } else {
      setMasterData([]);
      setFilteredData([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchText) {
      const lowered = searchText.toLowerCase();
      const f = masterData.filter(md => md.dataName?.toLowerCase().includes(lowered));
      setFilteredData(f);
    } else {
      setFilteredData(masterData);
    }
  }, [searchText, masterData]);

  const breadcrumb = [
    { title: <HomeOutlined />, path: "/app/dashboard" },
    { title: t("Admin"), path: "/app/admin/config" },
    { title: t("Master Data") }
  ];

  return (
    <div className="max-w-full p-3 md:p-4 lg:p-6">
      <div className="app-breadcrumb mb-4">
        <BreadCrumb items={breadcrumb} />
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-5 gap-3">
        <div className="flex items-center gap-3">
          <Title level={3} className="!m-0 !text-lg md:!text-xl !font-semibold text-gray-900">
            {t("Master Data")}
          </Title>
          <Tag color="blue" className="text-xs">{filteredData.length}</Tag>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder={t("Search master data...")}
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
            suffix={<SearchOutlined className="mr-2 text-gray-400" />}
            className="w-full md:w-72"
            size="middle"
            value={searchText}
          />
          <Button onClick={() => navigate("/app/admin/config")}>{t("Back")}</Button>
        </div>
      </div>

      {/* Master Data Grid */}
      {filteredData?.length > 0 ? (
        <Flex wrap gap={28} align="stretch">
          <MasterList dataList={filteredData} getData={fetchData} />
        </Flex>
      ) : (
        <div className="text-gray-500">{t("No master data available.")}</div>
      )}
    </div>
  );
};

export default AllMasterDataPage;
